import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Calendar, FileSearch, MessageCircle, Search, ArrowRight, Star, Award, Users, BedDouble, Stethoscope, Sparkles, ShieldCheck, HeartPulse } from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import { useSettings, digitsOnly } from "@/contexts/SettingsContext";
import { useContent } from "@/contexts/ContentContext";

const STAT_ICONS = [Stethoscope, Award, Users, BedDouble, Sparkles];

export default function Home() {
  const c = useContent();
  const s = useSettings();
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");

  const wa = digitsOnly(s.whatsapp);
  const emTel = digitsOnly(s.emergency_phone);

  useEffect(() => {
    Promise.all([
      api.get("/doctors"),
      api.get("/specialties"),
      api.get("/packages"),
      api.get("/testimonials"),
      api.get("/services"),
    ]).then(([d, sp, pk, t, sv]) => {
      setDoctors(d.data.filter((x) => x.featured).slice(0, 6));
      setSpecialties(sp.data);
      setPackages(pk.data.slice(0, 4));
      setTestimonials(t.data.slice(0, 6));
      setServices(sv.data.slice(0, 8));
    }).catch(() => {});
  }, []);

  const hero = c.hero || {};

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      {hero.visible !== false && (
        <section className="hero-bg relative overflow-hidden" data-testid="hero-section">
          <div className="orb" style={{ width: 460, height: 460, background: "#8B2D8B", top: -140, left: -120 }} />
          <div className="orb" style={{ width: 520, height: 520, background: "#2BA84A", bottom: -200, right: -160, animationDelay: "-5s" }} />

          <div className="container-x grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24 relative z-10">
            <div className="animate-fade-in-up">
              {hero.badge && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wider uppercase mb-6"
                     style={{ background: "rgba(139,45,139,0.08)", border: "1px solid rgba(139,45,139,0.2)", color: "#8B2D8B" }}
                     data-testid="hero-pill">
                  <Sparkles className="h-3 w-3" /> {hero.badge}
                </div>
              )}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight font-serif">
                <span className="text-cream">{hero.heading_line1}</span><br />
                <span className="gradient-text">{hero.heading_line2}</span>
              </h1>
              {hero.tagline && <p className="font-cursive text-3xl mt-4 text-brand-purple" data-testid="hero-tagline">{hero.tagline}</p>}
              <p className="mt-6 text-base sm:text-lg max-w-xl leading-relaxed text-mute">{hero.subtext}</p>

              <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-cta-group">
                {hero.emergency_enabled !== false && <a href={`tel:${emTel}`} className="btn-emergency" data-testid="hero-emergency-btn"><Phone className="h-4 w-4" /> {hero.emergency_label}</a>}
                {hero.book_enabled !== false && <Link to="/book-appointment" className="btn-primary" data-testid="hero-book-btn"><Calendar className="h-4 w-4" /> {hero.book_label}</Link>}
                {hero.second_opinion_enabled !== false && <Link to="/second-opinion" className="btn-secondary" data-testid="hero-second-opinion-btn"><FileSearch className="h-4 w-4" /> {hero.second_opinion_label}</Link>}
                {hero.whatsapp_enabled !== false && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="btn-outline" data-testid="hero-whatsapp-btn"><MessageCircle className="h-4 w-4" /> {hero.whatsapp_label}</a>}
              </div>

              {hero.search_enabled !== false && (
                <form onSubmit={(e) => { e.preventDefault(); window.location.href = `/doctors?q=${encodeURIComponent(search)}`; }}
                  className="mt-10 max-w-lg flex items-center gap-2 rounded-full p-1.5 bg-white"
                  style={{ border: "1px solid #e6e0ee", boxShadow: "0 12px 30px -18px rgba(139,45,139,0.35)" }}>
                  <Search className="h-5 w-5 text-mute ml-3" />
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder={hero.search_placeholder}
                    className="!border-0 !bg-transparent !shadow-none !ring-0 flex-1 !py-2"
                    data-testid="hero-search-input" />
                  <button type="submit" className="btn-primary !px-5 !py-2 text-sm" data-testid="hero-search-btn">Search</button>
                </form>
              )}

              <div className="mt-10 flex flex-wrap items-center gap-6 text-mute text-xs">
                {hero.trust_badge_1 && <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-green" /> {hero.trust_badge_1}</div>}
                {hero.trust_badge_2 && <div className="flex items-center gap-2"><HeartPulse className="h-4 w-4 text-brand-purple" /> {hero.trust_badge_2}</div>}
                {hero.trust_badge_3 && <div className="flex items-center gap-2"><Award className="h-4 w-4 text-gold" /> {hero.trust_badge_3}</div>}
              </div>
            </div>

            <div className="relative">
              <div className="card-3d rounded-[2rem] overflow-hidden">
                <img src={hero.image} alt="One Hospitals" className="w-full h-[520px] object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 card-clean p-3 flex items-center gap-3 max-w-[240px]" data-testid="hero-floating-nabh">
                <div className="logo-badge shrink-0 border border-slate-100"><img src={LOGO_URL} alt="" className="h-9 w-9 object-contain" /></div>
                <div className="leading-tight">
                  <div className="text-[10px] uppercase tracking-wider text-mute">{hero.trust_badge_1}</div>
                  <div className="font-semibold text-xs text-cream">Trusted by families</div>
                </div>
              </div>
              <div className="absolute -top-5 right-3 rounded-2xl px-4 py-3 text-white inline-flex items-center gap-3"
                   style={{ background: "linear-gradient(160deg, #34c25a 0%, #2BA84A 70%, #218a3c 100%)",
                            boxShadow: "0 18px 40px -18px rgba(43,168,74,0.6)", width: "fit-content" }}>
                <Phone className="h-4 w-4" />
                <div className="leading-tight">
                  <div className="text-[9px] uppercase tracking-wider opacity-90">24 × 7</div>
                  <div className="font-bold text-sm">Emergency</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ STATS BAR ============ */}
      {c.stats?.visible !== false && (
        <section className="border-y border-slate-100 bg-white" data-testid="stats-section">
          <div className="container-x py-10 grid grid-cols-2 md:grid-cols-5 gap-6">
            {(c.stats?.items || []).map((st, i) => {
              const Icon = STAT_ICONS[i % STAT_ICONS.length];
              return (
                <div key={i} className="text-center" data-testid={`stat-${i}`}>
                  <Icon className="h-5 w-5 mx-auto mb-3 text-brand-purple" />
                  <div className="stat-num">{st.num}</div>
                  <div className="text-xs uppercase tracking-wider text-mute mt-2">{st.label}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ============ ABOUT ============ */}
      {c.about?.visible !== false && (
        <section className="section" data-testid="about-section">
          <div className="container-x grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="card-3d rounded-[2rem] overflow-hidden">
                <img src={c.about?.image} alt="Doctors team" className="w-full h-[480px] object-cover" />
              </div>
              <div className="absolute bottom-6 right-6 card-clean p-5 max-w-[240px]">
                <Star className="h-5 w-5 text-gold fill-current" />
                <div className="text-2xl font-serif font-bold mt-1 text-cream">{c.about?.rating_value}</div>
                <div className="text-xs text-mute">{c.about?.rating_text}</div>
              </div>
            </div>
            <div>
              <span className="font-cursive text-2xl text-brand-purple">{c.about?.eyebrow}</span>
              <h2 className="text-4xl md:text-5xl font-bold font-serif mt-2 leading-[1.05] text-cream">{c.about?.heading}</h2>
              <p className="mt-5 text-mute leading-relaxed">{c.about?.body}</p>
              <div className="grid grid-cols-3 gap-4 mt-8">
                {(c.about?.cards || []).map((v, i) => (
                  <div key={i} className="card-clean p-4">
                    <div className="font-serif font-semibold text-brand-purple text-lg">{v.title}</div>
                    <div className="text-xs text-mute mt-1">{v.desc}</div>
                  </div>
                ))}
              </div>
              <Link to="/about" className="btn-primary mt-8" data-testid="about-learn-more-btn">{c.about?.cta_label} <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </section>
      )}

      {/* ============ SPECIALTIES ============ */}
      {c.specialties_section?.visible !== false && (
        <section className="section surface-purple-glow relative overflow-hidden" data-testid="specialties-section">
          <div className="container-x">
            <div className="max-w-2xl mb-12">
              <span className="font-cursive text-2xl text-brand-green">{c.specialties_section?.eyebrow}</span>
              <h2 className="text-4xl md:text-5xl font-serif font-bold mt-1 text-cream">{c.specialties_section?.heading}</h2>
              <p className="text-mute mt-4">{c.specialties_section?.subtext}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {specialties.slice(0, 17).map((sp) => (
                <Link key={sp.id} to={`/specialties#${sp.slug}`} className="card-clean card-shine p-5 text-center group" data-testid={`spec-card-${sp.slug}`}>
                  <div className="h-11 w-11 mx-auto rounded-2xl grid place-items-center transition group-hover:scale-105" style={{ background: "rgba(139,45,139,0.08)", border: "1px solid rgba(139,45,139,0.18)" }}>
                    <Stethoscope className="h-5 w-5 text-brand-purple" />
                  </div>
                  <div className="text-sm font-semibold mt-3 leading-tight text-cream">{sp.name}</div>
                </Link>
              ))}
              <Link to="/specialties" className="p-5 text-center flex flex-col items-center justify-center rounded-[1.25rem] text-white" style={{ background: "linear-gradient(160deg, #a13ba1 0%, #8B2D8B 70%, #742674 100%)" }} data-testid="view-all-specialties">
                <ArrowRight className="h-7 w-7 mb-1" />
                <div className="text-sm font-semibold">View all</div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ============ DOCTORS ============ */}
      {c.doctors_section?.visible !== false && (
        <section className="section" data-testid="doctors-section">
          <div className="container-x">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
              <div>
                <span className="font-cursive text-2xl text-brand-purple">{c.doctors_section?.eyebrow}</span>
                <h2 className="text-4xl md:text-5xl font-serif font-bold mt-1 text-cream">{c.doctors_section?.heading}</h2>
              </div>
              <Link to="/doctors" className="btn-outline" data-testid="view-all-doctors-btn">View all <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {doctors.map((d) => (
                <div key={d.id} className="card-clean overflow-hidden" data-testid={`doctor-card-${d.id}`}>
                  <div className="relative">
                    <img src={d.photo} alt={d.name} className="w-full h-60 object-cover" />
                    <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-[10px] font-semibold tracking-wider uppercase text-white" style={{ background: "rgba(139,45,139,0.9)" }}>
                      {d.experience_years}+ yrs
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="font-serif text-lg font-semibold text-cream">{d.name}</div>
                    <div className="text-sm text-brand-purple font-medium">{d.specialty}</div>
                    <div className="text-xs text-mute mt-1 font-mono">{d.qualification}</div>
                    <div className="text-xs text-mute mt-2">{(d.languages || []).slice(0, 3).join(" · ")}</div>
                    <div className="flex gap-2 mt-5">
                      <Link to={`/book-appointment?doctor=${d.id}`} className="btn-primary !py-2 !px-3 text-xs flex-1 justify-center" data-testid={`book-${d.id}`}>Book</Link>
                      <Link to={`/doctors/${d.id}`} className="btn-outline !py-2 !px-3 text-xs flex-1 justify-center" data-testid={`view-${d.id}`}>Profile</Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ SERVICES ============ */}
      {c.services_section?.visible !== false && (
        <section className="section surface-soft relative overflow-hidden" data-testid="services-section">
          <div className="container-x">
            <div className="max-w-2xl mb-12">
              <span className="font-cursive text-2xl text-brand-green">{c.services_section?.eyebrow}</span>
              <h2 className="text-4xl md:text-5xl font-serif font-bold mt-1 text-cream">{c.services_section?.heading}</h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              {services.map((sv) => (
                <div key={sv.id} className="card-clean p-6" data-testid={`service-card-${sv.id}`}>
                  <div className="h-11 w-11 rounded-2xl grid place-items-center mb-4" style={{ background: "rgba(43,168,74,0.1)", border: "1px solid rgba(43,168,74,0.2)" }}>
                    <HeartPulse className="h-5 w-5 text-brand-green" />
                  </div>
                  <div className="font-serif font-semibold text-cream text-lg">{sv.name}</div>
                  <div className="text-sm text-mute mt-2 line-clamp-3">{sv.description}</div>
                </div>
              ))}
            </div>
            <div className="text-center mt-10"><Link to="/services" className="btn-primary">All Services <ArrowRight className="h-4 w-4" /></Link></div>
          </div>
        </section>
      )}

      {/* ============ PACKAGES ============ */}
      {c.packages_section?.visible !== false && (
        <section className="section" data-testid="packages-section">
          <div className="container-x">
            <div className="max-w-2xl mb-12 text-center mx-auto">
              <span className="font-cursive text-2xl text-brand-green">{c.packages_section?.eyebrow}</span>
              <h2 className="text-4xl md:text-5xl font-serif font-bold mt-1 text-cream">{c.packages_section?.heading}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {packages.map((p, idx) => (
                <div key={p.id} className={`card-clean p-6 flex flex-col relative ${idx === 1 ? "ring-2 ring-brand-purple" : ""}`} data-testid={`pkg-card-${p.id}`}>
                  {idx === 1 && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: "linear-gradient(90deg, #8B2D8B, #b23bb2)" }}>Popular</div>}
                  <div className="text-[10px] uppercase tracking-wider text-brand-green font-semibold">{p.category}</div>
                  <div className="font-serif font-bold text-xl mt-2 text-cream">{p.name}</div>
                  <div className="stat-num !text-4xl mt-4">₹{p.price.toLocaleString("en-IN")}</div>
                  <ul className="text-sm text-mute mt-4 space-y-1.5 flex-1">
                    {(p.tests || []).slice(0, 4).map((t, i) => <li key={i} className="flex gap-2"><span className="text-brand-green">✓</span>{t}</li>)}
                    {(p.tests || []).length > 4 && <li className="text-mute opacity-60">+ {p.tests.length - 4} more</li>}
                  </ul>
                  <Link to={`/book-appointment?package=${encodeURIComponent(p.name)}`} className="btn-primary mt-5 justify-center" data-testid={`book-pkg-${p.id}`}>Book Package</Link>
                </div>
              ))}
            </div>
            <div className="text-center mt-10"><Link to="/packages" className="btn-outline">View all packages <ArrowRight className="h-4 w-4" /></Link></div>
          </div>
        </section>
      )}

      {/* ============ TESTIMONIALS ============ */}
      {c.testimonials_section?.visible !== false && (
        <section className="section testimonial-dark relative overflow-hidden" data-testid="testimonials-section">
          <div className="container-x">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="font-cursive text-2xl text-gold">{c.testimonials_section?.eyebrow}</span>
              <h2 className="text-4xl md:text-5xl font-serif font-bold mt-1 text-cream">{c.testimonials_section?.heading}</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="card-clean p-7" data-testid={`testimonial-${t.id}`}>
                  <div className="flex gap-0.5 mb-4 text-gold">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
                  <p className="text-ink leading-relaxed text-[15px] font-serif italic">“{t.review}”</p>
                  <div className="mt-5 pt-5 border-t border-slate-100">
                    <div className="font-semibold text-cream">{t.patient_name}</div>
                    <div className="text-xs text-mute mt-0.5">{t.treatment}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ FINAL CTA ============ */}
      {c.cta_section?.visible !== false && (
        <section className="container-x py-20">
          <div className="rounded-[2rem] relative overflow-hidden p-10 md:p-16"
               style={{ background: "linear-gradient(120deg, #8B2D8B 0%, #6d216d 55%, #2BA84A 130%)",
                        boxShadow: "0 40px 80px -30px rgba(139,45,139,0.45)" }}>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 relative z-10">
              <div className="max-w-xl">
                <span className="font-cursive text-2xl text-white/90">{c.cta_section?.eyebrow}</span>
                <h3 className="text-4xl md:text-5xl font-serif font-bold mt-1 text-white leading-tight">{c.cta_section?.heading}</h3>
                <p className="mt-4 text-white/85">{c.cta_section?.subtext}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <Link to="/book-appointment" className="btn-outline !bg-white !border-white" data-testid="cta-book"><Calendar className="h-4 w-4" /> {c.cta_section?.book_label}</Link>
                <a href={`tel:${emTel}`} className="btn-outline !bg-transparent !text-white !border-white/60" data-testid="cta-call"><Phone className="h-4 w-4" /> {c.cta_section?.call_label}</a>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
